import React, { createContext, useContext, useEffect, useState } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * NationContext — trauma-informed jurisdiction switcher.
 *
 *  - `nation` is 'ew' (England & Wales) or 'scotland'.
 *  - The choice is remembered in localStorage under `tfi_nation_v1`
 *    so a survivor doesn't have to re-pick on every guide.
 *  - When the nation changes, the page scrolls to the top so the
 *    filtered content is visible immediately.
 *  - Each explicit choice fires a fire-and-forget POST to
 *    /api/track/nation_ew or /api/track/nation_scotland so the admin
 *    can see how many survivors pick each nation. Anonymous — a
 *    simple counter, no user data.
 *
 * Pure client-side. No cookies, no tracking beyond the anonymous counter.
 */
const KEY = 'tfi_nation_v1';
const NationContext = createContext({ nation: 'ew', setNation: () => {} });

export function NationProvider({ children }) {
  const [nation, setNationState] = useState('ew');

  useEffect(() => {
    try {
      const stored = window.localStorage.getItem(KEY);
      if (stored === 'scotland' || stored === 'ew') setNationState(stored);
    } catch {
      /* ignore private-mode storage errors */
    }
  }, []);

  const setNation = (value) => {
    if (value !== 'ew' && value !== 'scotland') return;
    setNationState(value);
    try {
      window.localStorage.setItem(KEY, value);
    } catch {
      /* ignore */
    }
    // Fire-and-forget: anonymous nation-pick counter for admin stats.
    if (BACKEND_URL) {
      try {
        fetch(`${BACKEND_URL}/api/track/nation_${value}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: '{}',
          keepalive: true,
        }).catch(() => {});
      } catch {
        /* ignore */
      }
    }
    // Scroll to top so the newly-visible content is right there.
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  };

  return (
    <NationContext.Provider value={{ nation, setNation }}>
      {children}
    </NationContext.Provider>
  );
}

export function useNation() {
  return useContext(NationContext);
}

/** Render children only when the visitor has chosen England & Wales. */
export function IfEnglandWales({ children }) {
  const { nation } = useNation();
  if (nation !== 'ew') return null;
  return <>{children}</>;
}

/** Render children only when the visitor has chosen Scotland. */
export function IfScotland({ children }) {
  const { nation } = useNation();
  if (nation !== 'scotland') return null;
  return <>{children}</>;
}
